import {IPrice, PriceSyncStatus, PricePresentation} from '~shared/types';

/**
 * Resolve what to put on screen for one price.
 *
 * The project-wide conditions come first because they say nothing about any individual price:
 * if Odoo is unreachable or a recalculation never landed, no number in the project can be
 * trusted, whatever the payload says. Below that the price speaks only for itself — one
 * component Odoo could not price does not put its neighbours in doubt, so a valid sibling
 * keeps showing its amount.
 */
export function resolvePriceView(
	reachable: boolean,
	status: PriceSyncStatus,
	price: IPrice | undefined,
): PricePresentation {
	if (!reachable) {
		return {state: 'unavailable'};
	}
	if (status === 'syncing') {
		return {state: 'syncing'};
	}
	if (status === 'unconfirmed') {
		return {state: 'unconfirmed'};
	}
	if (!price) {
		return {state: 'none'};
	}
	if (price.valid === false) {
		return {state: 'invalid'};
	}
	const value = price.unit_price?.excl_tax;
	return value != null ? {state: 'known', value} : {state: 'unknown'};
}

/**
 * Resolve what to put on screen for a project total. Same rules, but the amount is the rollup
 * itself rather than a unit price. Odoo withholds and flags the rollup as soon as a line
 * feeding it could not be priced, so `valid` covers completeness as well: a total that
 * silently omits a line would read as a complete quote for less money.
 */
export function resolveTotalPriceView(
	reachable: boolean,
	status: PriceSyncStatus,
	price: IPrice | undefined,
): PricePresentation {
	if (!reachable) {
		return {state: 'unavailable'};
	}
	if (status === 'syncing') {
		return {state: 'syncing'};
	}
	if (status === 'unconfirmed') {
		return {state: 'unconfirmed'};
	}
	if (!price) {
		return {state: 'none'};
	}
	if (price.valid === false) {
		return {state: 'invalid'};
	}
	return price.excl_tax != null ? {state: 'known', value: price.excl_tax} : {state: 'unknown'};
}
